import apiClient from './apiClient';
import { ENDPOINTS } from './apiConfig';

/**
 * ------------------------------------------------------------------
 * SERVICIO DE RETENCIONES IVA
 * ------------------------------------------------------------------
 * Maneja las operaciones CRUD para retenciones de IVA
 * Basado en la documentación:
 * POST   /api/retencion/iva/header          -> Crear cabecera de retención IVA
 * DELETE /api/retencion/iva/header/{id}     -> Eliminar cabecera de retención IVA
 * GET    /api/retencion/iva/header/{id}     -> Obtener cabecera de retención IVA
 * POST   /api/retencion/iva/items           -> Agregar ítem de retención IVA
 * POST   /api/retencion/iva/items/update    -> Actualizar ítem de retención IVA
 * DELETE /api/retencion/iva/items/{id}      -> Eliminar ítem de retención IVA por cabecera
 * ------------------------------------------------------------------
 */

/**
 * Normaliza los datos de la cabecera de retención desde la API
 * La API devuelve solo el ID de la cabecera creada
 */
const normalizeHeaderData = (apiData) => {
    if (!apiData) return null;

    return {
        idcabezeras_iva: apiData.idcabezeras_iva || null,
        // Alias para facilitar uso
        id: apiData.idcabezeras_iva || null
    };
};

/**
 * Normaliza los datos de un ítem de retención desde la API
 */
const normalizeItemData = (apiData) => {
    if (!apiData) return null;

    return {
        empresa_rif: apiData.empresa_rif || '',
        cabezeras_iva_idcabezeras_iva: apiData.cabezeras_iva_idcabezeras_iva || null,
        fecha: apiData.fecha || '',
        numero: apiData.numero || '',
        numero_control: apiData.numero_control || '',
        doc_afectado: apiData.doc_afectado || '',
        base: apiData.base || 0,
        exento: apiData.exento || 0,
        tipo_idtipo: apiData.tipo_idtipo || null,
        iva_alicuota: apiData.iva_alicuota || 0,
        numero_serie: apiData.numero_serie || ''
    };
};

/**
 * Normaliza un documento de la API al formato esperado por el formulario
 * Mantiene la estructura de la API y agrega campos calculados
 * @param {Object} apiItem - Item desde la API
 * @param {number} retencionDefault - Porcentaje de retención por defecto (de la cabecera)
 * @returns {Object} Documento normalizado
 */
const normalizeDocumentFromAPI = (apiItem, retencionDefault = 0) => {
    const base = parseFloat(apiItem.base || 0);
    const alicuota = parseFloat(apiItem.iva_alicuota || 0);
    const exento = parseFloat(apiItem.exento || 0);
    const impuestoCalculado = (base * alicuota / 100);
    const total = base + exento + impuestoCalculado;
    const retencionPct = parseFloat(apiItem.retencion || retencionDefault);
    const retenido = (impuestoCalculado * retencionPct / 100);

    return {
        ...apiItem, // Mantener todas las propiedades originales
        id: apiItem.id || apiItem.idretencion_iva || apiItem.iditems_iva || `temp-${Date.now()}-${Math.floor(Math.random() * 10000)}`,
        descripcion: apiItem.descripcion || 'Factura',
        fecha_doc: apiItem.fecha_doc || apiItem.fecha,
        numero: apiItem.numero,
        numero_control: apiItem.numero_control,
        doc_afectado: apiItem.doc_afectado || '',
        base: apiItem.base,
        iva_alicuota: apiItem.iva_alicuota,
        impuesto: impuestoCalculado.toFixed(2), // Campo calculado para mostrar en tabla
        exento: apiItem.exento || '0',
        total: total.toFixed(2), // Campo calculado
        retencion: retencionPct,
        retenido: retenido.toFixed(2) // Campo calculado
    };
};

const ivaService = {
    /**
     * CABECERA DE RETENCIÓN
     */

    /**
     * Crear cabecera de retención IVA
     * @param {Object} headerData - Datos de la cabecera
     * @param {string} headerData.empresa_rif - RIF de la empresa
     * @param {string} headerData.proveedor_rif - RIF del proveedor
     * @param {string} headerData.fecha - Fecha de la retención
     * @param {string} headerData.periodo_fiscal - Período fiscal
     * @param {string} headerData.numero_orden_pago - Número de orden de pago
     * @returns {Promise<Object>} Cabecera creada
     */
    createHeader: async (headerData) => {
        try {
            const response = await apiClient.post(ENDPOINTS.IVA_HEADER, headerData);
            const data = response.data || response;
            return normalizeHeaderData(data);
        } catch (error) {
            console.error('Error creating IVA header:', error);
            throw error;
        }
    },

    /**
     * Obtener cabecera de retención IVA por ID con sus ítems
     * @param {number} id - ID de la cabecera
     * @param {string} empresa_rif - RIF de la empresa
     * @returns {Promise<Object>} Datos completos del comprobante (cabecera + ítems)
     */
    getHeaderById: async (id, empresa_rif) => {
        try {
            const queryParams = empresa_rif ? `?empresa_rif=${empresa_rif}` : '';
            const response = await apiClient.get(`${ENDPOINTS.IVA_HEADER_BY_ID(id)}${queryParams}`);
            return response.data || response;
        } catch (error) {
            console.error(`Error fetching IVA header ${id}:`, error);
            throw error;
        }
    },

    /**
     * Eliminar cabecera de retención IVA
     * @param {number} id - ID de la cabecera
     * @returns {Promise<Object>} Respuesta de eliminación
     */
    deleteHeader: async (id) => {
        try {
            const response = await apiClient.delete(ENDPOINTS.IVA_HEADER_BY_ID(id));
            return response.data || response;
        } catch (error) {
            console.error(`Error deleting IVA header ${id}:`, error);
            throw error;
        }
    },

    /**
     * Actualizar cabecera de retención IVA
     * @param {number} id - ID de la cabecera
     * @param {Object} headerData - Datos de la cabecera a actualizar
     * @param {string} headerData.empresa_rif - RIF de la empresa
     * @param {string} headerData.fecha - Fecha de la retención
     * @param {string} headerData.periodo_fiscal - Período fiscal
     * @param {string} headerData.numero_orden_pago - Número de orden de pago
     * @param {number} headerData.anulada - Flag de anulación (0 o 1)
     * @returns {Promise<Object>} Cabecera actualizada
     */
    updateHeader: async (id, headerData) => {
        try {
            const response = await apiClient.put(ENDPOINTS.IVA_HEADER_UPDATE(id), headerData);
            const data = response.data || response;
            return normalizeHeaderData(data);
        } catch (error) {
            console.error(`Error updating IVA header ${id}:`, error);
            throw error;
        }
    },


    /**
     * Anular retención IVA (actualización simple)
     * Usa el nuevo endpoint PUT /api/retencion/iva/header/{id} para marcar como anulada
     * 
     * @param {number} headerId - ID de la cabecera a anular
     * @param {string} empresaRif - RIF de la empresa
     * @returns {Promise<Object>} Respuesta de la actualización
     */
    annulRetencion: async (headerId, empresaRif) => {
        try {
            console.log(`Anulando retención ${headerId}...`);

            // Obtener datos actuales del header para preservar otros campos
            const headerData = await ivaService.getHeaderById(headerId, empresaRif);
            const firstItem = Array.isArray(headerData) ? headerData[0] : headerData;

            if (!firstItem) {
                throw new Error('Retención no encontrada');
            }

            // Preparar payload con todos los campos necesarios
            const updatePayload = {
                empresa_rif: empresaRif,
                fecha: firstItem.fecha,
                periodo_fiscal: firstItem.periodo_fiscal,
                numero_orden_pago: firstItem.numero_orden_pago || '',
                anulada: 1  // ← Marcar como anulada
            };

            console.log('Actualizando header con anulada: 1', updatePayload);

            // Actualizar usando el nuevo endpoint PUT
            await ivaService.updateHeader(headerId, updatePayload);

            console.log(`Retención ${headerId} anulada exitosamente`);

            return {
                success: true,
                id: headerId,
                message: 'Retención anulada exitosamente'
            };

        } catch (error) {
            console.error('Error anulando retención:', error);
            throw error;
        }
    },

    /**
     * Obtener todas las cabeceras de retención IVA (resumen)
     * @param {Object} params - Parámetros de filtrado opcionales
     * @param {string} params.empresa_rif - RIF de la empresa
     * @param {number} params.page - Número de página (opcional)
     * @param {number} params.per_page - Elementos por página (opcional)
     * @param {string} params.order_by - Campo para ordenar (opcional)
     * @param {string} params.order_dir - Dirección de orden: asc/desc (opcional)
     * @returns {Promise<Array>} Lista de retenciones IVA
     */
    getAll: async (params = {}) => {
        try {
            const queryParams = new URLSearchParams(params).toString();
            const url = queryParams ? `${ENDPOINTS.IVA_SUMMARY}?${queryParams}` : ENDPOINTS.IVA_SUMMARY;
            const response = await apiClient.get(url);
            return response.data || response;
        } catch (error) {
            console.error('Error fetching IVA retenciones:', error);
            throw error;
        }
    },

    /**
     * ÍTEMS DE RETENCIÓN
     */

    /**
     * Agregar ítem de retención IVA
     * @param {Object} itemData - Datos del ítem
     * @param {string} itemData.empresa_rif - RIF de la empresa
     * @param {number} itemData.cabezeras_iva_idcabezeras_iva - ID de la cabecera
     * @param {string} itemData.fecha - Fecha del documento
     * @param {string} itemData.numero - Número de factura/documento
     * @param {string} itemData.numero_control - Número de control
     * @param {string} itemData.doc_afectado - Documento afectado
     * @param {number} itemData.base - Base imponible
     * @param {number} itemData.exento - Monto exento
     * @param {number} itemData.tipo_idtipo - ID del tipo de documento
     * @param {number} itemData.iva_alicuota - Alícuota de IVA (ej: 16)
     * @param {string} itemData.numero_serie - Número de serie
     * @returns {Promise<Object>} Ítem creado
     */
    createItem: async (itemData) => {
        try {
            const response = await apiClient.post(ENDPOINTS.IVA_ITEMS, itemData);
            const responseData = response.data || response;

            // Caso 1: El servidor devuelve {ok: true, data: {...}}
            if (responseData.data) {
                return normalizeItemData(responseData.data);
            }

            // Caso 2: El servidor devuelve solo el ID {idretencion_iva: 3} (Sandbox)
            if (responseData.idretencion_iva) {
                return normalizeItemData({
                    ...itemData,
                    iditems_iva: responseData.idretencion_iva
                });
            }

            // Caso 3: El servidor devuelve el objeto directamente
            return normalizeItemData(responseData);
        } catch (error) {
            console.error('Error creating IVA item:', error);
            throw error;
        }
    },

    /**
     * Actualizar ítem de retención IVA
     * @param {Object} itemData - Datos del ítem a actualizar
     * @returns {Promise<Object>} Ítem actualizado
     */
    updateItem: async (itemData) => {
        try {
            const response = await apiClient.post(ENDPOINTS.IVA_ITEMS_UPDATE, itemData);
            const data = response.data || response;
            return normalizeItemData(data);
        } catch (error) {
            console.error('Error updating IVA item:', error);
            throw error;
        }
    },


    /**
     * Eliminar un ítem específico de retención IVA
     * @param {number} id - ID del item a eliminar
     * @param {string} empresaRif - RIF de la empresa
     * @returns {Promise<Object>} Respuesta de eliminación
     */
    deleteItem: async (id, empresaRif) => {
        try {
            const response = await apiClient.delete(ENDPOINTS.IVA_ITEM_DELETE(id), {
                params: { empresa_rif: empresaRif }
            });
            return response.data || response;
        } catch (error) {
            console.error(`Error deleting IVA item ${id}:`, error);
            throw error;
        }
    },

    /**
     * Eliminar todos los ítems de una cabecera de retención IVA
     * @param {number} headerId - ID de la cabecera
     * @returns {Promise<Object>} Respuesta de eliminación
     */
    deleteAllItems: async (headerId) => {
        try {
            const response = await apiClient.delete(ENDPOINTS.IVA_ITEMS_BY_ID(headerId));
            return response.data || response;
        } catch (error) {
            console.error(`Error deleting all IVA items for header ${headerId}:`, error);
            throw error;
        }
    },

    /**
     * Obtener items de una cabecera de retención
     * @param {number|string} headerId - ID de la cabecera
     * @param {string} empresa_rif - RIF de la empresa (opcional, pero recomendado)
     * @returns {Promise<Array>} Lista de items
     */
    getItemsByHeaderId: async (headerId, empresa_rif = null) => {
        try {
            // Asegurar que el ID sea numérico simple para la URL si es necesario, 
            // aunque el backend debería manejar strings. Pero por si acaso quitamos ceros a la izquierda
            const cleanId = parseInt(headerId, 10);

            const queryParams = empresa_rif ? `?empresa_rif=${empresa_rif}` : '';
            const url = `${ENDPOINTS.IVA_ITEMS_BY_ID(cleanId)}${queryParams}`;

            console.log(`Fetching items from: ${url}`);

            const response = await apiClient.get(url);
            const data = response.data || response;
            // Manejar si devuelve { data: [...] } o [...] directament
            const items = Array.isArray(data) ? data : (data.data || []);
            return items.map(normalizeItemData);
        } catch (error) {
            console.error(`Error fetching IVA items for header ${headerId}:`, error);
            // Si falla (ej: 404 porque no hay items), retornar array vacío para no romper el flujo
            return [];
        }
    },

    /**
     * Crear retención completa (1 cabecera + múltiples ítems) usando el endpoint /full
     * 
     * Este endpoint crea la cabecera y todos los items en una sola llamada.
     * 
     * @param {Object} retencionData - Datos completos de la retención
     * @param {Object} retencionData.header - Datos de la cabecera (sin items)
     * @param {Array} retencionData.items - Array de documentos
     * @returns {Promise<Object>} Retención creada con header e items
     */
    createRetencion: async (retencionData) => {
        try {
            const { header, items } = retencionData;

            if (!header) {
                throw new Error('Se requiere la información de la cabecera');
            }

            if (!items || items.length === 0) {
                throw new Error('Se requiere al menos un documento para crear la retención');
            }

            // Preparar el payload según el formato esperado por el endpoint /full
            // Todos los parámetros van en el body, no en query params
            const payload = {
                empresa_rif: header.empresa_rif,
                proveedor_rif: header.proveedor_rif,
                fecha: header.fecha,
                periodo_fiscal: header.periodo_fiscal,
                numero_orden_pago: header.numero_orden_pago || '',
                items: items.map(item => ({
                    fecha: item.fecha,
                    numero: item.numero,
                    numero_control: item.numero_control,
                    doc_afectado: item.doc_afectado || '',
                    base: item.base,
                    exento: item.exento || 0,
                    tipo_idtipo: item.tipo_idtipo || 1,
                    iva_alicuota: item.iva_alicuota,
                    numero_serie: item.numero_serie || ''
                }))
            };

            console.log('Creando retención IVA completa con endpoint /full...', payload);

            const response = await apiClient.post(ENDPOINTS.IVA_FULL, payload);

            const data = response.data || response;
            console.log('Retención IVA creada exitosamente:', data);

            return {
                header: {
                    idcabezeras_iva: data.idcabezeras_iva,
                    id: data.idcabezeras_iva
                },
                success: true,
                totalItems: items.length,
                savedItems: items.length,
                failedItems: 0
            };
        } catch (error) {
            console.error('Error creating complete IVA retencion:', error);
            throw error;
        }
    },

    /**
     * Obtener retención completa (cabecera + ítems)
     * @param {number} headerId - ID de la cabecera
     * @returns {Promise<Object>} Retención completa
     */
    getRetencionById: async (headerId) => {
        try {
            const header = await ivaService.getHeaderById(headerId);
            // Aquí podrías agregar lógica para obtener los ítems si hay un endpoint específico
            return {
                header,
                items: [] // TODO: Implementar cuando haya endpoint para obtener ítems
            };
        } catch (error) {
            console.error(`Error fetching complete IVA retencion ${headerId}:`, error);
            throw error;
        }
    },

    /**
     * Obtener PDF de comprobante de retención IVA
     * GET /api/retencion/iva/{id}/pdf
     */
    getPdf: async (id, empresa_rif) => {
        try {
            const queryParams = empresa_rif ? `?empresa_rif=${empresa_rif}` : '';
            const response = await apiClient.get(`${ENDPOINTS.IVA_PDF(id)}${queryParams}`, {
                responseType: 'blob'
            });
            return response.data;
        } catch (error) {
            console.error(`Error downloading PDF for IVA ${id}:`, error);
            throw error;
        }
    },

    /**
     * Verificar si un número de control ya existe
     * GET /api/retencion/iva/check-control
     * @param {string} proveedorRif - RIF del proveedor
     * @param {string} numeroControl - Número de control a verificar
     * @param {number} excludeHeader - ID de cabecera a excluir (opcional, para edición)
     * @returns {Promise<boolean>} - true si existe, false si no
     */
    checkControlNumber: async (proveedorRif, numeroControl, excludeHeader = null) => {
        try {
            const params = {
                proveedor_rif: proveedorRif,
                numero_control: numeroControl
            };
            if (excludeHeader) params.exclude_header = excludeHeader;

            const response = await apiClient.get('/api/retencion/iva/check-control', { params });
            return response.data?.exists || response?.exists || false;
        } catch (error) {
            console.error('Error checking control number:', error);
            return false; // En caso de error, asumir que no existe
        }
    },

    /**
     * Verificar si un número de factura ya existe
     * GET /api/retencion/iva/check-invoice
     * @param {string} proveedorRif - RIF del proveedor
     * @param {string} numero - Número de factura a verificar
     * @param {number} excludeHeader - ID de cabecera a excluir (opcional, para edición)
     * @returns {Promise<boolean>} - true si existe, false si no
     */
    checkInvoiceNumber: async (proveedorRif, numero, excludeHeader = null) => {
        try {
            const params = {
                proveedor_rif: proveedorRif,
                numero: numero
            };
            if (excludeHeader) params.exclude_header = excludeHeader;

            const response = await apiClient.get('/api/retencion/iva/check-invoice', { params });
            return response.data?.exists || response?.exists || false;
        } catch (error) {
            console.error('Error checking invoice number:', error);
            return false; // En caso de error, asumir que no existe
        }
    }
};

export default ivaService;
export { normalizeDocumentFromAPI };
